import React from "react";
import { useTranslation } from "react-i18next";
import type { CsvImportPlan, MigrationEntity } from "../../api/migrationsApi";
import tailwindClassnames from "../common/TailwindClassnames";
import { CountRow, formatNumber } from "../migrations/MigrationReport";
import {
  csvImportNoticeText,
  csvSkipReasonText,
  entityCopy,
} from "./csvImport";

/**
 * Renders a CSV import report (AKL-813) — the dry run before confirming, and the result
 * after. Same shape both times, so one renderer, as with `MigrationReport`.
 *
 * Entity-agnostic: it renders whichever of the `sensors` / `locations` / `deployments`
 * sections the report carries, so the locations and deployments imports (AKL-814/815) reuse
 * it as is. Like the WI report it never says "Cameras": the row label is the app's existing
 * entity name.
 */

/** The order sections are shown in when a report carries more than one. */
const ENTITY_ORDER: MigrationEntity[] = ["sensors", "locations", "deployments"];

interface CsvImportReportProps {
  report: CsvImportPlan;
  /** True when this is the outcome of an apply rather than a preview of one. */
  applied?: boolean;
}

const CsvImportReport: React.FC<CsvImportReportProps> = ({
  report,
  applied = false,
}) => {
  const { t, i18n } = useTranslation();
  const number = (value: number) => formatNumber(value, i18n.language);

  // Read defensively: the report comes back from a stored job row.
  const blocking = report.blocking ?? [];
  const warnings = report.warnings ?? [];
  const skippedRows = report.skipped_rows ?? [];
  const missingColumns = report.columns?.missing ?? [];
  const fileColumns = report.file?.columns ?? [];
  const sections = ENTITY_ORDER.filter(
    (entity) => report[entity] !== undefined,
  );
  const skippedTotal = sections.reduce(
    (total, entity) => total + (report[entity]?.skipped ?? 0),
    0,
  );

  // A blocking notice is always emitted alone: the counts are all zero and there are no
  // skipped rows, so the counts section would only say "0 new, 0 already in akili" under an
  // alert that has just explained why nothing ran. The alert and the file name carry the
  // whole story in that case.
  const blocked = blocking.length > 0;
  const showCounts = !blocked && sections.length > 0;

  return (
    <div className="space-y-4">
      {blocking.length > 0 && (
        <div
          className="rounded-lg border border-primary-orange/40 bg-primary-orange/10 p-3"
          role="alert"
        >
          <p className={tailwindClassnames.warningHeader}>
            {applied
              ? t("This import could not be completed")
              : t("This import cannot run yet")}
          </p>
          <ul className="mt-1.5 list-disc space-y-1 pl-5">
            {blocking.map((notice, index) => (
              <li
                key={`${notice.code}-${index}`}
                className={tailwindClassnames.helperText}
              >
                {csvImportNoticeText(notice, t)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {showCounts && (
        <section>
          <h3 className={tailwindClassnames.basicSubHeading}>
            {applied ? t("What was imported") : t("What will be imported")}
          </h3>
          <div className="mt-1.5 divide-y divide-off-white-dark/60 dark:divide-storm-blue-border">
            {sections.map((entity) => {
              const counts = report[entity];
              if (!counts) return null;
              return (
                <CountRow
                  key={entity}
                  label={entityCopy(entity).label}
                  create={counts.create}
                  matched={counts.matched}
                  note={
                    // A raw number, as "{{count}} columns" passes one: i18next types
                    // `count` as a number (it drives plural forms).
                    counts.skipped > 0
                      ? t("{{count}} skipped", { count: counts.skipped })
                      : undefined
                  }
                />
              );
            })}
          </div>
        </section>
      )}

      {/* Every skipped row named with its reason. An import that quietly drops a tenth of
          someone's file while reporting success is the failure this prevents. */}
      {skippedRows.length > 0 && (
        <details
          open
          className="rounded-lg border border-off-white-dark/70 p-2.5 dark:border-storm-blue-border"
        >
          <summary
            className={`${tailwindClassnames.basicLabel} cursor-pointer`}
            data-testid="csv-report-skipped-rows"
          >
            {t("Skipped rows")}
          </summary>
          <div className="mt-1.5 overflow-x-auto">
            <table className={tailwindClassnames.tableContainer}>
              <tbody>
                {skippedRows.map((entry, index) => {
                  const reason = csvSkipReasonText(entry, t);
                  return (
                    <tr
                      key={`${entry.row}-${entry.code}-${index}`}
                      className={tailwindClassnames.tableCardRowStatic}
                    >
                      <th
                        scope="row"
                        className={`${tailwindClassnames.tableCardHeadCell} whitespace-nowrap`}
                      >
                        {/* Plain, not grouped: the user is matching it to a spreadsheet's
                            row numbers, which are never written as "1,234". */}
                        {t("Row {{row}}", { row: entry.row })}
                      </th>
                      <td
                        className={tailwindClassnames.tableCardCell}
                        title={reason}
                      >
                        {reason}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {report.skipped_rows_truncated && (
            <p className={`${tailwindClassnames.helperText} mt-1.5`}>
              {t("Showing {{shown}} of {{total}} skipped rows.", {
                shown: number(skippedRows.length),
                total: number(skippedTotal),
              })}
            </p>
          )}
        </details>
      )}

      {warnings.length > 0 && (
        <section>
          <h3 className={tailwindClassnames.basicSubHeading}>
            {t("Worth checking")}
          </h3>
          <ul className="mt-1.5 list-disc space-y-1 pl-5">
            {warnings.map((notice, index) => (
              <li
                key={`${notice.code}-${index}`}
                className={tailwindClassnames.helperText}
              >
                {csvImportNoticeText(notice, t)}
              </li>
            ))}
          </ul>
        </section>
      )}

      <details className="rounded-lg border border-off-white-dark/70 p-2.5 dark:border-storm-blue-border">
        <summary
          className={`${tailwindClassnames.basicLabel} cursor-pointer`}
          data-testid="csv-report-file-details"
        >
          {t("File details")}
        </summary>
        <ul className="mt-1.5 space-y-0.5">
          <li className={tailwindClassnames.helperText}>{report.file?.name}</li>
          {/* A structural blocker (wrong delimiter, missing required column, too many rows)
              returns before the rows are counted, so `rows` is 0, `columns` may be the
              unsplit header, and `missing` includes the required column the alert has just
              named — none of which would be true of the fixed file. Only the name is
              reliable then. When nothing blocks, every required column is present, so
              `missing` really is the optional ones; the API already leaves ignored columns
              (such as project_id) out of it. */}
          {!blocked && (
            <li className={tailwindClassnames.helperText}>
              {t("{{count}} rows", { count: report.file?.rows ?? 0 })} —{" "}
              {t("{{count}} columns", { count: fileColumns.length })}
            </li>
          )}
          {!blocked && missingColumns.length > 0 && (
            <li className={tailwindClassnames.helperText}>
              {t("Optional columns not in this file: {{columns}}", {
                columns: missingColumns.join(", "),
              })}
            </li>
          )}
        </ul>
      </details>
    </div>
  );
};

export default CsvImportReport;
